import { useEffect, useState } from 'react';
import type { UpdateGraphSettingsInput } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { TextField } from '../../components/ui/Field.js';
import { useGraphSettings, useUpdateGraphSettings } from './api.js';

export function GraphSettingsPage(): JSX.Element {
  const settingsQuery = useGraphSettings();
  const updateMutation = useUpdateGraphSettings();
  const settings = settingsQuery.data;

  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [mailSender, setMailSender] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [clearSecret, setClearSecret] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setTenantId(settings.tenantId ?? '');
    setClientId(settings.clientId ?? '');
    setMailSender(settings.mailSender ?? '');
    setClientSecret('');
    setClearSecret(false);
  }, [settings]);

  const handleSave = (): void => {
    setSaved(false);
    const input: UpdateGraphSettingsInput = {
      tenantId: tenantId.trim() || null,
      clientId: clientId.trim() || null,
      mailSender: mailSender.trim() || null,
    };
    // Only send the secret when entering a new one or explicitly clearing it.
    if (clearSecret) {
      input.clientSecret = null;
    } else if (clientSecret.trim()) {
      input.clientSecret = clientSecret.trim();
    }
    updateMutation.mutate(input, {
      onSuccess: () => {
        setSaved(true);
        setClientSecret('');
        setClearSecret(false);
      },
    });
  };

  const saveError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? 'Failed to save Graph settings.'
        : null;

  return (
    <div>
      <PageHeader
        title="Microsoft Graph"
        description="Application credentials used for outgoing email and SharePoint file storage."
      />

      <Card>
        <DataState
          isLoading={settingsQuery.isLoading}
          isError={settingsQuery.isError}
          error={settingsQuery.error}
          onRetry={() => void settingsQuery.refetch()}
          isEmpty={false}
        >
          {settings ? (
            <div className="max-w-2xl space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={settings.configured ? 'green' : 'amber'}>
                  {settings.configured ? 'Configured' : 'Not configured'}
                </Badge>
                {settings.managedInDatabase ? <Badge tone="blue">Managed in app</Badge> : null}
                {!settings.canStoreSecrets ? (
                  <span className="text-xs text-amber-600">
                    No encryption key on the server — the client secret cannot be stored from the
                    UI.
                  </span>
                ) : null}
              </div>

              <p className="text-sm text-slate-500">
                These credentials come from the Entra app registration the PSA owns. A value saved
                here is stored encrypted in the database and overrides the matching environment
                variable. Leave a field blank to fall back to the environment value.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label="Tenant ID"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
                <TextField
                  label="Client ID"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Application (client) ID"
                />
                <TextField
                  label="Default mail sender"
                  value={mailSender}
                  onChange={(e) => setMailSender(e.target.value)}
                  placeholder="invoices@nscatalyst.com"
                />
                <div>
                  <TextField
                    label="Client secret"
                    type="password"
                    value={clientSecret}
                    onChange={(e) => {
                      setClientSecret(e.target.value);
                      if (e.target.value) setClearSecret(false);
                    }}
                    disabled={clearSecret || !settings.canStoreSecrets}
                    placeholder={
                      settings.hasClientSecret ? '•••••••• (stored)' : 'Enter a client secret'
                    }
                  />
                  {settings.hasClientSecret ? (
                    <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                      <input
                        type="checkbox"
                        checked={clearSecret}
                        onChange={(e) => {
                          setClearSecret(e.target.checked);
                          if (e.target.checked) setClientSecret('');
                        }}
                      />
                      Clear the stored secret (fall back to the environment value)
                    </label>
                  ) : null}
                </div>
              </div>

              {saveError ? <p className="text-sm text-rose-600">{saveError}</p> : null}
              {saved ? <p className="text-sm text-emerald-600">Settings saved.</p> : null}

              <div className="flex items-center gap-2">
                <Button onClick={handleSave} isLoading={updateMutation.isPending}>
                  Save settings
                </Button>
              </div>
            </div>
          ) : null}
        </DataState>
      </Card>
    </div>
  );
}
